import React, { useState, useEffect } from 'react'
import * as Papa from 'papaparse'
import moment from 'moment'
import { ICustomer, CustomersApi, ICustomerFilter, OptInStatus } from '@getgreenline/customers'
import {
  CustomTablePaginationV2,
  TPaginationPosition,
} from '../../../../components/CustomTablePaginationV2'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { Checkbox, Menu, message, Skeleton } from 'antd'
import { GrPrice, parseErrorMsg } from '../../../../utilities/helpers'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CustomerViewAndEditModal } from '../../../../components/CustomerViewAndEditModal'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { API, PermissionNames } from '@getgreenline/homi-shared'
import { ICursorPagination, IBaseCursorPaginationFilter } from '@getgreenline/shared'
import { flatten } from 'lodash'
import { Key } from 'antd-v4/lib/table/interface'
import { CustomerTotalSpend } from '../../../../components/CustomerTotalSpend'
import { Column, CustomTable, FilterOption, SearchField } from '../../../../components/CustomTable'
import { AppMainView } from '../../../../components/AppView'
import { ComposeMessage } from './ComposeMessage'
import AppHeader from '../../../../components/AppView/Header'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { RangePickerValue } from 'antd/lib/date-picker/interface'
import { Alert } from 'antd-v4'
import { InfoCircleFilled } from '@ant-design/icons'
import { getQuota } from '@getgreenline/marketing'
import endpoints from '../../../../constants/Endpoints'
import { useStash } from './hooks/useStash'
import { HelpLinks } from '../../../../constants/ExternalLinks'
import { CustomerSearch, MFERenderedPages } from '@getgreenline/customer-search'
import { CustomersPageMFE } from '../../../../components/MfeParcels/CustomersPage'
import {
  openCustomerMFEModal,
  addToRecipientListInMFE,
  clearSelectedGlobalCustomersInMFE,
} from '../../../../mfeEvents/customer'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import { useRefState } from '../../../../utilities/hooks/useRefState'
import dayjs, { Dayjs } from 'dayjs'
import { ComposeMessageV2 } from './ComposeMessageV2'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface CustomerListTableRecord {
  key: string
  name: JSX.Element
  location: JSX.Element
  email: JSX.Element
  phone: JSX.Element
  totalSpend: JSX.Element
  loyaltyGroup: JSX.Element
  loyaltyBalance: JSX.Element
  dateAdded: JSX.Element
}

enum AddOnCustomerListFilterType {
  NAME = 'Name',
  EMAIL = 'Email',
  PHONE = 'Phone',
  LOYALTY_TIER_ID = 'Loyalty Tier',
}

interface CustomerListTableExport {
  Name: string
  Location: string | null
  Email: string | null
  Phone: string | null
  'Total Spend': number | string
  'Loyalty Group': string | null
  'Loyalty Balance': number | string
  'Date Added': string
}

const _CustomerList = (props: Props) => {
  const dateTwoYearsAgo = moment().subtract(2, 'years').startOf('day')
  const currentDate = moment()
  const [limit, setLimit] = useState<number>(20)
  const [isSelectCustomerView, setIsSelectCustomerView] = useState<boolean>(true)
  const [recipientList, setRecipientList] = useState<ICustomer[]>([])
  const [selectedCustomers, setSelectedCustomers] = useState<ICustomer[]>([])

  const [recipientListV2, _setRecipientListV2] = useState<CustomerSearch.API.GlobalCustomer[]>([])
  const [selectedGlobalCustomers, setSelectedGlobalCustomers] = useState<
    CustomerSearch.API.GlobalCustomer[]
  >([])
  const [displayedMfeColumns, setDisplayedMfeColumns] = useState<string[]>()
  const [displayedLastPurchaseDateRange, setDisplayedLastPurchaseDateRange] = useState<
    [Dayjs | null, Dayjs | null]
  >([null, null])

  useEffect(() => {
    const [startDate, endDate] = displayedLastPurchaseDateRange
    if (startDate && endDate) {
      const twoYearsAgo = dayjs().subtract(2, 'year').subtract(1, 'day')
      if (startDate.isBefore(twoYearsAgo) || endDate.isBefore(twoYearsAgo)) {
        setBannersHeight(bannersHeight + 47)
        return setIsDateFilterGoingBackBeyondTwoYears(true)
      }
    }
    setBannersHeight(47)
    return setIsDateFilterGoingBackBeyondTwoYears(false)
  }, [displayedLastPurchaseDateRange])

  const [displayedGlobalCustomersRef, setDisplayedGlobalCustomers] = useRefState<
    CustomerSearch.API.GlobalCustomer[]
  >([])
  const [globalPaginationRef, setGlobalPagination] = useRefState<CustomerSearch.API.Pagination>({
    __typename: 'Pagination',
    nextPageCursor: null,
    prevPageCursor: null,
  })
  const [multiPageSelectedGlobalCustomerListRef, setMultiPageSelectedGlobalCustomerList] =
    useRefState<Map<string, CustomerSearch.API.GlobalCustomer[]>>(new Map())

  const [customers, setCustomers] = useState<ICustomer[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const locations = props.currentCompanyStore?.locations
  const [loyaltyTiers, setLoyaltyTiers] = useState<{ id: number; name: string }[]>([])
  const [filteredLocationIds, setFilteredLocationIds] = useState<number[]>([])
  const [modalCustomerId, setModalCustomerId] = useState<string>()
  const [multiPageSelectedCustomerList, setMultiPageSelectedCustomerList] = useState<
    Map<string, ICustomer[]>
  >(new Map())

  const [pagination, setPagination] = useState<ICursorPagination>({
    nextCursor: null,
    prevCursor: null,
  })

  const [currentCursor, setCurrentCursor] = useState<string | null>(null)
  const [searchFields, setSearchFields] = useState<SearchField[]>([])
  const [appliedSearchFields, setAppliedSearchFields] = useState<SearchField[]>([])
  const [paginationReset, setPaginationFirstPage] = useState<boolean>(false)
  const [smsQuota, setSMSQuota] = useState(-1)
  const [isWithinQuota, setIsWithinQuota] = useState(true)
  const [stashedCustomerList, setStashedCustomerList] = useStash<ICustomer>([])
  const [recipientIdList, setRecipientIdList] = useState<Set<Key>>(new Set())
  const [freshRecipientList, setFreshRecipientList] = useState<ICustomer[]>([])

  const [isDateFilterGoingBackBeyondTwoYears, setIsDateFilterGoingBackBeyondTwoYears] =
    useState<boolean>(false)
  const [isDateFilterCleared, setIsDateFilterCleared] = useState<boolean>(false)
  const [shouldResetCursor, setShouldResetCursor] = useState<boolean>(false)
  const [bannersHeight, setBannersHeight] = useState<number>(47)

  const canAccessLoyalty =
    !props.currentCompanyStore?.canUseExternalLoyalty &&
    !!props.currentCompanyStore?.company?.permissions?.canAccessLoyalty

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      hide: false,
      width: 125,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      hide: false,
      width: 125,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      hide: false,
      width: 125,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      hide: false,
      width: 125,
    },
    {
      title: 'Total Spend',
      dataIndex: 'totalSpend',
      key: 'totalSpend',
      hide: false,
      width: 125,
    },
    {
      title: 'Loyalty Group',
      dataIndex: 'loyaltyGroup',
      key: 'loyaltyGroup',
      hide: false,
      width: 125,
    },
    {
      title: 'Loyalty Points',
      dataIndex: 'loyaltyBalance',
      key: 'loyaltyBalance',
      hide: false,
      width: 125,
    },
    {
      title: 'Date Added',
      dataIndex: 'dateAdded',
      key: 'dateAdded',
      hide: false,
      width: 125,
    },
  ]
  const [appliedRangePickerValue, setAppliedRangePickerValue] = useState<RangePickerValue>([
    dateTwoYearsAgo,
    currentDate,
  ])
  const [displayedColumns] = useState<Column[]>(
    canAccessLoyalty ? columns : columns.filter((column) => column.dataIndex !== 'loyaltyBalance'),
  )
  const [selectedColumns, setSelectedColumns] = useState<Column[]>(
    displayedColumns.filter((column) => column.hide === false),
  )

  const columnDropdownOverlay = () => {
    return (
      <Menu className='ml-1'>
        {displayedColumns.map((column) => {
          return (
            <Menu.Item key={column.key}>
              <Checkbox
                checked={!column.hide}
                onClick={() => {
                  column.hide = !column.hide
                  setSelectedColumns(displayedColumns.filter((column) => column.hide === false))
                }}
              >
                {column.title}
              </Checkbox>
            </Menu.Item>
          )
        })}
      </Menu>
    )
  }

  const canEditCustomers = props.currentUserStore!.hasPermissionAtEntity(
    PermissionNames.CAN_EDIT_CUSTOMERS,
    props.currentCompanyStore!.company!.id,
  )

  const canViewCustomerTotalSpend = props.currentUserStore!.hasPermissionAtEntity(
    PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND,
    props.currentCompanyStore!.company!.id,
  )

  useEffect(() => {
    const fetchData = async () => {
      if (props.currentCompanyStore?.canUsePlatformCustomersOutreach === undefined) {
        await props.currentCompanyStore!.getCanUsePlatformCustomersSearchOutreach()
      }

      if (!props.currentCompanyStore?.canUsePlatformCustomersOutreach) {
        getAndSetCustomers()
      }

      getSMSQuota()
      getAndSetLoyaltyTiers()
    }

    fetchData()
  }, [])

  useEffect(() => {
    const recipients = stashedCustomerList.filter((customer) => recipientIdList.has(customer.id))
    setFreshRecipientList(recipients)
  }, [recipientIdList, stashedCustomerList])

  const checkCompliance = () => {
    const startDate = appliedRangePickerValue[0]
    if (startDate) {
      setIsDateFilterCleared(false)
      setIsDateFilterGoingBackBeyondTwoYears(startDate < dateTwoYearsAgo)
    } else {
      setIsDateFilterCleared(true)
    }
  }

  const clearListHandler = () => {
    setMultiPageSelectedCustomerList(new Map())
    setRecipientList([])
    setSelectedCustomers([])
    setFreshRecipientList([])
    setRecipientIdList(new Set())
  }

  const clearListHandlerV2 = () => {
    setMultiPageSelectedGlobalCustomerList(new Map())
    setRecipientListV2([])
    setSelectedGlobalCustomers([])
    clearSelectedGlobalCustomersInMFE()
    setRecipientIdList(new Set())
  }

  const getAndSetCustomers = async () => {
    const currentCustomers = await getCustomers({
      locationIds: filteredLocationIds,
      rangePicker: appliedRangePickerValue,
      searchFields,
    })
    setCustomers(currentCustomers || [])
  }

  const getCustomers = async (
    {
      locationIds,
      searchFields,
      rangePicker,
      cursor,
    }: {
      locationIds: number[]
      searchFields?: SearchField[]
      rangePicker?: RangePickerValue
      cursor?: string | null
    } = {
      locationIds: [],
      rangePicker: [],
    },
  ) => {
    setCurrentCursor(cursor || null)
    try {
      setIsLoading(true)
      const options: ICustomerFilter = {
        name: searchFields?.find(
          (searchfield) => searchfield?.field === AddOnCustomerListFilterType.NAME,
        )?.value,
        email: searchFields?.find(
          (searchfield) => searchfield?.field === AddOnCustomerListFilterType.EMAIL,
        )?.value,
        phone: searchFields?.find(
          (searchfield) => searchfield?.field === AddOnCustomerListFilterType.PHONE,
        )?.value,
        /** optInStatus
         * Customers that have implicitly opted in will have canSendSmsMarketing set to null
         * Customers that have explicitly opted in will have canSendSmsMarketing set to true
         * Customers that have explicitly opted out will have canSendSmsMarketing set to false
         * Both will include both customerns with canSendSmsMarketing set to null or true
         */
        optInStatus: OptInStatus.IMP_EXP_OPT_IN,
      }

      let loyaltyTierIdStrings =
        searchFields?.find(
          (searchfield) => searchfield?.field === AddOnCustomerListFilterType.LOYALTY_TIER_ID,
        )?.value || []

      if (!Array.isArray(loyaltyTierIdStrings)) loyaltyTierIdStrings = [loyaltyTierIdStrings]
      const loyaltyTierIds = loyaltyTierIdStrings.map((e) => parseInt(e))

      const payload: ICustomerFilter & IBaseCursorPaginationFilter = {
        hasPhoneNumber: true,
        limit,
        locationIds,
        loyaltyTierIds,
        lastPurchaseStartDate: rangePicker?.length ? rangePicker[0]?.toDate() : undefined,
        lastPurchaseEndDate: rangePicker?.length ? rangePicker[1]?.toDate() : undefined,
        ...options,
      }

      if (cursor) {
        payload.cursor = cursor
      }
      const { data, pagination } = await CustomersApi.getCursorPaginatedCustomers(
        props.currentCompanyStore!.company!.id,
        payload,
      )
      setStashedCustomerList(data)
      setPagination(pagination)
      return data
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while searching for a customer')
    } finally {
      setIsLoading(false)
    }
  }

  const getSMSQuota = async () => {
    if (!endpoints.AUTHZ_GATEWAY_URL) {
      throw new Error('AUTHZ_GATEWAY_URL is not configured for this environment')
    }
    try {
      const response = await getQuota(
        endpoints.AUTHZ_GATEWAY_URL,
        props.currentCompanyStore!.company!.id,
      )
      setSMSQuota(response.quantity)
    } catch (error) {
      message.error('Unable to retrieve quota for this company')
      setSMSQuota(0)
    }
  }

  const getAndSetLoyaltyTiers = async () => {
    try {
      const availableLoyaltyTiers = await props.currentCompanyStore?.getLoyaltyTiers()

      const noLoyaltyTier = {
        id: 0,
        name: 'No Loyalty Tier',
      }

      if (availableLoyaltyTiers) {
        setLoyaltyTiers([noLoyaltyTier, ...availableLoyaltyTiers])
        const defaultSearchFields: SearchField[] = [
          {
            field: 'Loyalty Tier',
            value: [noLoyaltyTier, ...availableLoyaltyTiers].map((loyaltyTier) =>
              loyaltyTier.id.toString(),
            ),
          },
        ]
        setSearchFields(defaultSearchFields)
      }
    } catch (error) {
      message.error(
        parseErrorMsg(error) ||
          'Unable to retrieve loyalty tiers, please refresh the page and try again',
      )
    }
  }

  const setDefaultFilters = async () => {
    const defaultSearchFields: SearchField[] = [
      {
        field: 'Loyalty Tier',
        value: loyaltyTiers.map((loyaltyTier) => loyaltyTier.id.toString()),
      },
    ]
    setFilteredLocationIds([])
    setSearchFields(defaultSearchFields)
    setAppliedSearchFields(defaultSearchFields)
    setAppliedRangePickerValue([dateTwoYearsAgo, currentDate])
    setIsDateFilterCleared(false)
    setIsDateFilterGoingBackBeyondTwoYears(false)
    const currentCustomers = await getCustomers({
      locationIds: filteredLocationIds,
      rangePicker: [dateTwoYearsAgo, currentDate],
      searchFields,
    })
    setCustomers(currentCustomers || [])
  }

  const addToSelectedCustomerList = (customerList: ICustomer[]) => {
    const newMultiPageSelectedCustomerList = new Map(multiPageSelectedCustomerList)
    const currentPageCursor = !pagination.prevCursor ? 'firstPage' : pagination.prevCursor
    newMultiPageSelectedCustomerList.set(currentPageCursor, customerList)
    setMultiPageSelectedCustomerList(newMultiPageSelectedCustomerList)
    setSelectedCustomers(flatten([...newMultiPageSelectedCustomerList.values()]))
  }

  const addToSelectedGlobalCustomerList = (customerList: CustomerSearch.API.GlobalCustomer[]) => {
    const newMultiPageSelectedCustomerList = new Map(multiPageSelectedGlobalCustomerListRef.current)
    const currentPageCursor =
      globalPaginationRef?.current?.prevPageCursor === null
        ? 'firstPage'
        : (globalPaginationRef?.current?.prevPageCursor as string)
    newMultiPageSelectedCustomerList.set(currentPageCursor, customerList)
    setMultiPageSelectedGlobalCustomerList(newMultiPageSelectedCustomerList)
    const newSelectedGlobalCustomers = flatten([...newMultiPageSelectedCustomerList.values()])
    setSelectedGlobalCustomers(newSelectedGlobalCustomers)
  }

  const removeCustomerFromRecipientList = (customerId: string) => {
    setRecipientIdList((currentRecipientIdList) => {
      const newRecipientIdList = new Set(currentRecipientIdList)
      if (newRecipientIdList.has(customerId)) newRecipientIdList.delete(customerId)
      return newRecipientIdList
    })
    setFreshRecipientList(
      stashedCustomerList.filter((customer) => recipientIdList.has(customer.id)),
    )
    const customerIndex = recipientList.findIndex((recipient) => recipient.id == customerId)
    if (customerIndex === -1) return
    const copyOfRecipientList = [...recipientList]
    copyOfRecipientList.splice(customerIndex, 1)
    setRecipientList(copyOfRecipientList)
    removeCustomerFromMultiPageRecipientList(customerId)
  }

  const removeCustomerFromRecipientListV2 = (customerId: string) => {
    setRecipientIdList((currentRecipientIdList) => {
      const newRecipientIdList = new Set(currentRecipientIdList)
      if (newRecipientIdList.has(customerId)) newRecipientIdList.delete(customerId)
      return newRecipientIdList
    })
    const customerIndex = recipientListV2.findIndex((recipient) => recipient.id == customerId)
    if (customerIndex === -1) return
    const copyOfRecipientList = [...recipientListV2]
    copyOfRecipientList.splice(customerIndex, 1)
    setRecipientListV2(copyOfRecipientList)
    removeCustomerFromMultiPageRecipientList(customerId)
  }

  const removeCustomerFromMultiPageRecipientList = (customerId: string) => {
    for (const [currentPageCursor, customers] of multiPageSelectedCustomerList.entries()) {
      const customerIndexToRemove = customers.findIndex((customer) => customer.id === customerId)
      if (customerIndexToRemove === -1) continue
      const newMap = new Map(multiPageSelectedCustomerList)
      customers.splice(customerIndexToRemove, 1)
      newMap.set(currentPageCursor, customers)
      setMultiPageSelectedCustomerList(newMap)
      break
    }
  }

  const addToRecipientListHandler = () => {
    // Add to recipient list, clear selected customers
    const list = [...recipientList, ...selectedCustomers]
    const mappedList = new Map<string, ICustomer>()
    const recipientsAndCurrentSelectedCustomersIdList: Set<Key> = new Set()
    list.forEach((customer) => {
      recipientsAndCurrentSelectedCustomersIdList.add(customer.id)
      mappedList.set(customer.id, customer)
    })

    setRecipientIdList(recipientsAndCurrentSelectedCustomersIdList)
    setRecipientList([...mappedList.values()])
    setSelectedCustomers([])
  }

  const addToRecipientListHandlerV2 = () => {
    // Add to recipient list, clear selected customers
    const list = [...recipientListV2, ...selectedGlobalCustomers]
    const mappedList = new Map<string, CustomerSearch.API.GlobalCustomer>()
    const recipientsAndCurrentSelectedCustomersIdList: Set<Key> = new Set()
    list.forEach((customer) => {
      recipientsAndCurrentSelectedCustomersIdList.add(customer.id)
      mappedList.set(customer.id, customer)
    })
    setRecipientIdList(recipientsAndCurrentSelectedCustomersIdList)
    setRecipientListV2([...mappedList.values()])
  }

  //Use this function instead of the setter to keep both lists in sync
  const setRecipientListV2 = (customers: CustomerSearch.API.GlobalCustomer[]) => {
    _setRecipientListV2(customers)
    addToRecipientListInMFE(customers.map((customer) => customer.id))
  }

  const resetFilters = async () => {
    setPaginationFirstPage(true)
    setDefaultFilters()
  }

  const setModalCustomerView = async (customer: ICustomer | undefined) => {
    await applyFilters({
      locationIds: filteredLocationIds,
      searchFields: [],
      rangePicker: appliedRangePickerValue,
      cursor: currentCursor,
    })
    setModalCustomerId(customer?.id)
  }

  const applyFilters = async ({
    locationIds,
    searchFields,
    rangePicker,
    cursor,
  }: {
    locationIds: number[]
    searchFields?: SearchField[]
    rangePicker?: RangePickerValue
    cursor?: string | null
  }) => {
    const currentValidCursor = shouldResetCursor ? null : cursor
    const currentCustomers = await getCustomers({
      locationIds: locationIds || filteredLocationIds,
      searchFields,
      rangePicker,
      cursor: currentValidCursor,
    })
    setCustomers(currentCustomers || [])
    if (shouldResetCursor) setPaginationFirstPage(true)
    setShouldResetCursor(false)
    checkCompliance()
  }

  const applyFilterButtonHandler = async (
    locationIds: number[],
    searchFields: SearchField[],
    rangePicker?: RangePickerValue,
  ) => {
    setFilteredLocationIds(locationIds)
    setPaginationFirstPage(true)
    setAppliedRangePickerValue(rangePicker || [])
    const newSearchFields = searchFields.map((searchField) => {
      return { ...searchField }
    })
    setAppliedSearchFields(newSearchFields)
    await applyFilters({
      locationIds,
      searchFields: newSearchFields,
      rangePicker: rangePicker || [],
    })
  }

  const allAvailableFilterOptions: AddOnCustomerListFilterType[] = [
    AddOnCustomerListFilterType.NAME,
    AddOnCustomerListFilterType.EMAIL,
    AddOnCustomerListFilterType.PHONE,
    AddOnCustomerListFilterType.LOYALTY_TIER_ID,
  ]

  const filterOptions: FilterOption[] = [
    {
      name: AddOnCustomerListFilterType.LOYALTY_TIER_ID,
      enum: loyaltyTiers.map((loyaltyTier: { id: number; name: string }) => ({
        key: loyaltyTier.id,
        text: loyaltyTier.name,
        value: loyaltyTier.id.toString(),
      })),
      optional: false,
      dropdownVisible: false,
    },
    {
      name: AddOnCustomerListFilterType.NAME,
      enum: [],
      optional: true,
    },
    {
      name: AddOnCustomerListFilterType.EMAIL,
      enum: [],
      optional: true,
    },
    {
      name: AddOnCustomerListFilterType.PHONE,
      enum: [],
      optional: true,
    },
  ]

  const dataSource: CustomerListTableRecord[] = customers.map((customer) => {
    return {
      key: customer.id,
      name: setModalCustomerId ? (
        <div
          className='cursor-pointer'
          onClick={() => {
            if (!setModalCustomerId) return
            setModalCustomerId(customer.id)
          }}
          style={{ color: 'var(--blue)' }}
        >
          {customer.name}
        </div>
      ) : (
        <div>{customer.name}</div>
      ),
      location: <div>{customer.locationName}</div>,
      email: <div>{customer.email}</div>,
      phone: <div>{customer.phone}</div>,
      totalSpend: (
        <CustomerTotalSpend
          canViewCustomerTotalSpend={canViewCustomerTotalSpend}
          totalSpend={customer.totalSpend}
        />
      ),
      loyaltyGroup: <div>{customer.loyaltyTierName || 'No loyalty'}</div>,
      loyaltyBalance: <div>{customer.creditBalance + ' points'}</div>,
      dateAdded: <div>{moment(customer.createDate).format('MMM DD YYYY')}</div>,
    }
  })

  const rawDataSource: CustomerListTableExport[] = customers.map((customer) => {
    return {
      Name: customer.name,
      Location: customer.locationName,
      Email: customer.email,
      Phone: customer.phone,
      'Total Spend': canViewCustomerTotalSpend
        ? GrPrice.convertCentToDollar(customer.totalSpend)
        : 'No permissions',
      'Loyalty Group': customer.loyaltyTierName || 'No loyalty',
      'Loyalty Balance': customer.creditBalance,
      'Date Added': moment(customer.createDate).format('MMM DD YYYY'),
    }
  })

  const recipientCustomerIds = (recipientList || []).map((recipient) => recipient.id)

  const selectedRowKeys = [
    ...new Set([...selectedCustomers.map((customer) => customer.id), ...recipientCustomerIds]),
  ]

  const handleGlobalCustomerCheck = (globalCustomerIds: string[]) => {
    const selectedGlobalCustomers = displayedGlobalCustomersRef.current.filter((customer) => {
      return globalCustomerIds.includes(customer.id)
    })
    addToSelectedGlobalCustomerList(selectedGlobalCustomers)
  }

  const rowSelection = {
    onChange: (selectedRowKeys: Key[]) => {
      const customerIds = [...new Set(selectedRowKeys)]
      const selectedCustomers = customers.filter((customer) => customerIds.includes(customer.id))
      addToSelectedCustomerList(selectedCustomers)
    },
    selectedRowKeys,
    getCheckboxProps: (selected: CustomerListTableRecord) => {
      const isWithinQuota = selectedRowKeys.length <= smsQuota

      setIsWithinQuota(isWithinQuota)
      return {
        disabled:
          recipientCustomerIds.includes(selected.key) ||
          (!selectedRowKeys.includes(selected.key) && selectedRowKeys.length >= smsQuota),
      }
    },
  }

  const _export = (fileName: string, json: any) => {
    const csvString = Papa.unparse(json)

    // Reference: https://github.com/mholt/PapaParse/issues/175
    const blob = new Blob([csvString])
    if (window.navigator.msSaveBlob) {
      window.navigator.msSaveBlob(blob, fileName + '.csv')
    } else {
      const a = window.document.createElement('a')
      a.href = (window.URL as any).createObjectURL(blob, { type: 'text/plain' } as any)
      a.download = fileName + '.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  const exportCSV = () => {
    if (rawDataSource) {
      const columnTitles = {}
      selectedColumns.map((column) => {
        columnTitles[column.title] = true
      })
      const filteredData = rawDataSource.map((row) => {
        const displayedKeysInRow = {}
        for (const key in row) {
          if (columnTitles[key]) {
            displayedKeysInRow[key] = row[key]
          }
        }
        return displayedKeysInRow
      })
      _export('customer-outreach', filteredData)
    }
  }

  const exportCSVV2 = () => {
    const updatedDisplayedMfeColumns = displayedMfeColumns?.map((column) =>
      column === 'firstName' ? 'name' : column,
    )

    const filteredGlobalCustomers = displayedGlobalCustomersRef.current.map((customer) => {
      const filteredCustomer = {}
      updatedDisplayedMfeColumns?.forEach((column) => {
        if (
          column === 'name' &&
          customer.hasOwnProperty('firstName') &&
          customer.hasOwnProperty('lastName')
        ) {
          filteredCustomer['name'] = `${customer.firstName} ${customer.lastName}`
        } else if (customer.hasOwnProperty(column)) {
          filteredCustomer[column] = customer[column]
        }
      })
      return filteredCustomer
    })

    _export('customer-outreach', filteredGlobalCustomers)
  }

  const getRecipientCount = () => {
    if (props?.currentCompanyStore?.canUsePlatformCustomersOutreach) {
      return recipientListV2.length > 0 ? `(${recipientListV2.length})` : ''
    }
    return recipientList.length > 0 ? `(${recipientList.length})` : ''
  }
  return (
    <>
      <AppHeader
        title='Outreach'
        backVisible={!isSelectCustomerView}
        onBackClick={() => {
          getSMSQuota()
          setIsSelectCustomerView(true)
        }}
        backButtonTitle='Find more'
      >
        {isSelectCustomerView && (
          <div className='d-flex'>
            <HelpLinkMenu
              helpLinkMenus={{
                href: HelpLinks.OUTREACH_SMS,
                title: 'Customer Outreach',
              }}
            />
            <BlazeButton
              buttonProps={{
                icon: 'download',
                onClick: props.currentCompanyStore?.canUsePlatformCustomersOutreach
                  ? exportCSVV2
                  : exportCSV,
                className: 'ml-2 mr-2',
              }}
              titleProps={{
                title: 'Export',
              }}
            />
            <BlazeButton
              buttonProps={{
                onClick: props?.currentCompanyStore?.canUsePlatformCustomersOutreach
                  ? addToRecipientListHandlerV2
                  : addToRecipientListHandler,
                className: 'mr-2',
                disabled: !isWithinQuota,
              }}
              titleProps={{
                title: `Add to list ${getRecipientCount()}`,
              }}
            />
            <BlazeButton
              buttonProps={{
                onClick: props?.currentCompanyStore?.canUsePlatformCustomersOutreach
                  ? clearListHandlerV2
                  : clearListHandler,
                className: 'mr-2',
                disabled: !isWithinQuota,
              }}
              titleProps={{
                title: 'Clear list',
              }}
            />
            {props?.currentCompanyStore?.canUsePlatformCustomersOutreach ? (
              <BlazeButton
                buttonProps={{
                  type: recipientListV2.length > 0 ? 'primary' : 'default',
                  disabled: recipientListV2.length === 0,
                  onClick: async () => {
                    setIsSelectCustomerView(!isSelectCustomerView)
                    setSelectedGlobalCustomers([])
                  },
                }}
                titleProps={{
                  title: 'Compose message',
                }}
              />
            ) : (
              <BlazeButton
                buttonProps={{
                  disabled: recipientList.length === 0,
                  type: recipientList.length > 0 ? 'primary' : 'default',
                  onClick: async () => {
                    setIsSelectCustomerView(!isSelectCustomerView)
                    setSelectedCustomers([])
                    await setDefaultFilters()
                  },
                }}
                titleProps={{
                  title: 'Compose message',
                }}
              />
            )}
          </div>
        )}
      </AppHeader>
      <AppMainView className='background-grey intercom-pb-2'>
        {isSelectCustomerView ? (
          <>
            <Skeleton
              className='custom-single-line-alert-skeleton'
              loading={smsQuota === -1}
              title={false}
              paragraph={{ rows: 1, width: '100%' }}
              active
            />
            {(isDateFilterCleared || isDateFilterGoingBackBeyondTwoYears) && (
              <Alert
                type='warning'
                showIcon
                className='mb-2'
                icon={<InfoCircleFilled />}
                message="Make sure you’re compliant with Canada's Anti-Spam Legislation"
              />
            )}
            {smsQuota === 0 && (
              <Alert
                type='warning'
                showIcon
                className='mb-2'
                icon={<InfoCircleFilled />}
                message={
                  <>
                    <span>{`${smsQuota} messages remaining, `}</span>
                    <span>please contact</span>
                    <a href='mailto:sales@getgreenline.co'> sales@getgreenline.co </a>
                    <span>for more</span>
                  </>
                }
              />
            )}

            {smsQuota > 0 && !(selectedRowKeys.length >= smsQuota) && (
              <Alert
                message={`${smsQuota} messages left to be sent.`}
                type='info'
                showIcon
                icon={<InfoCircleFilled />}
                className='mb-2'
              />
            )}
            {smsQuota > 0 && selectedRowKeys.length >= smsQuota && (
              <Alert
                type='warning'
                showIcon
                className='mb-2'
                icon={<InfoCircleFilled />}
                message={
                  <>
                    <span>{`${smsQuota} messages remaining and ${smsQuota} recipients selected; `}</span>
                    <span>please contact</span>
                    <a href='mailto:sales@getgreenline.co'> sales@getgreenline.co </a>
                    <span>for more</span>
                  </>
                }
              />
            )}

            {props.currentCompanyStore?.canUsePlatformCustomersOutreach === undefined ? (
              <LoadingAnimation />
            ) : (
              <>
                {props.currentCompanyStore.canUsePlatformCustomersOutreach ? (
                  <CustomersPageMFE
                    componentHeight={`calc(100% - ${bannersHeight}px)`}
                    setDisplayedGlobalCustomers={setDisplayedGlobalCustomers}
                    setDisplayedColumns={(columns) => {
                      setDisplayedMfeColumns(columns)
                    }}
                    selectedGlobalCustomers={selectedGlobalCustomers}
                    setGlobalPagination={setGlobalPagination}
                    handleGlobalCustomerCheck={handleGlobalCustomerCheck}
                    canViewCustomerTotalSpend={canViewCustomerTotalSpend}
                    canAccessLoyalty={canAccessLoyalty}
                    canEditCustomers={canEditCustomers}
                    companyId={props.currentCompanyStore.company!.id}
                    basename={`/company/${props.currentCompanyStore.company?.id}/dashboard/customers/outreach`}
                    actions={{
                      onCustomerNameClick: (customer: CustomerSearch.API.GlobalCustomer) => {
                        setModalCustomerId(customer.id)
                      },
                      onDeleteCustomer: async (customer: CustomerSearch.API.GlobalCustomer) => {
                        try {
                          await API.deleteCustomer(
                            props.currentCompanyStore!.company!.id,
                            customer.id,
                          )
                          openCustomerMFEModal({
                            message: 'Customer deleted successfully',
                            severity: 'success',
                          })
                        } catch (error) {
                          openCustomerMFEModal({
                            message:
                              parseErrorMsg(error) ||
                              'An error occurred while deleting the customer',
                            severity: 'error',
                          })
                        }
                      },
                    }}
                    parentPage={MFERenderedPages.OUTREACH}
                    locations={props.currentCompanyStore?.locations || []}
                    loyaltyTiers={props.currentCompanyStore?.sortedLoyaltyTiers || []}
                    setDisplayedLastPurchaseDateRange={setDisplayedLastPurchaseDateRange}
                  />
                ) : (
                  <div className='outreach-customer-list-table'>
                    <CustomTablePaginationV2
                      changePage={(cursor) =>
                        applyFilters({
                          locationIds: filteredLocationIds,
                          searchFields: appliedSearchFields,
                          rangePicker: appliedRangePickerValue,
                          cursor,
                        })
                      }
                      setPaginationRows={() =>
                        applyFilters({
                          locationIds: filteredLocationIds,
                          searchFields: appliedSearchFields,
                          rangePicker: appliedRangePickerValue,
                        })
                      }
                      data={customers}
                      paginationPosition={TPaginationPosition.BOTTOM}
                      pagination={pagination}
                      limit={limit}
                      setLimit={setLimit}
                      paginationReset={paginationReset}
                      setPaginationFirstPage={setPaginationFirstPage}
                    >
                      <CustomTable
                        applyFilterButtonHandler={applyFilterButtonHandler}
                        resetFilter={resetFilters}
                        dataSource={dataSource}
                        columns={columns}
                        selectedColumns={selectedColumns}
                        isLoading={isLoading}
                        rowSelection={rowSelection}
                        columnDropdownOverlay={columnDropdownOverlay}
                        allAvailableFilterOptions={allAvailableFilterOptions}
                        filterOptions={filterOptions}
                        verticalScroll={'65vh'}
                        searchFields={searchFields}
                        setSearchFields={setSearchFields}
                        rangePickerValue={appliedRangePickerValue}
                        onRangePickerChange={(value) => {
                          setShouldResetCursor(true)
                          setAppliedRangePickerValue(value)
                        }}
                        rangePickerLimits={{ maxEndDate: moment().endOf('day') }}
                      />
                    </CustomTablePaginationV2>
                  </div>
                )}
              </>
            )}
            {!!modalCustomerId && (
              <CustomerViewAndEditModal
                canViewCustomerTotalSpend={canViewCustomerTotalSpend}
                customerId={modalCustomerId}
                canSaveCustomer={canEditCustomers}
                onHide={() => setModalCustomerView(undefined)}
                onSubmit={async () => {
                  await setModalCustomerView(undefined)
                  const customerList =
                    (await getCustomers({
                      locationIds: filteredLocationIds,
                      searchFields,
                      rangePicker: appliedRangePickerValue,
                      cursor: currentCursor,
                    })) || []
                  setCustomers(customerList)
                }}
              />
            )}
          </>
        ) : props.currentCompanyStore?.canUsePlatformCustomersOutreach ? (
          <ComposeMessageV2
            removeCustomerFromRecipientList={removeCustomerFromRecipientListV2}
            globalCustomers={recipientListV2}
            clearListHandler={clearListHandlerV2}
          />
        ) : (
          <ComposeMessage
            removeCustomerFromRecipientList={removeCustomerFromRecipientList}
            customers={freshRecipientList}
            clearListHandler={clearListHandler}
          />
        )}
      </AppMainView>
    </>
  )
}

export const CustomerList = inject(
  'currentUserStore',
  'currentCompanyStore',
)(observer(_CustomerList))
